import React from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Chip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SmartToyIcon from "@mui/icons-material/SmartToy";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "0",
  },
  configCard: {
    borderRadius: "12px",
    border: "1px solid #e0e0e0",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
  },
  cardHeader: {
    backgroundColor: "#f0f7ff",
    padding: "20px 24px",
    borderBottom: "1px solid #bbdefb",
  },
  cardContent: {
    padding: "24px !important",
  },
  headerTitle: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  fieldContainer: {
    marginBottom: "24px",
  },
  textField: {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
    },
    "& .MuiInputBase-input": {
      fontWeight: 500,
    },
  },
  fieldLabel: {
    fontWeight: 600,
    marginBottom: "8px",
    color: theme.palette.text.primary,
  },
  fieldDescription: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginBottom: "12px",
  },
  valueChip: {
    marginTop: "8px",
    fontWeight: 600,
    backgroundColor: "#e8f5e8",
    color: "#2e7d32",
  },
  exampleBox: {
    marginTop: "16px",
    padding: "16px",
    backgroundColor: "#fff3e0",
    borderRadius: "8px",
    border: "1px solid #ffcc02",
  },
  exampleTitle: {
    fontWeight: 600,
    color: "#e65100",
    marginBottom: "8px",
  },
  configGrid: {
    "& .MuiGrid-item": {
      display: "flex",
      flexDirection: "column",
    },
  },
  iconContainer: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "12px",
  },
  matchWinIcon: {
    backgroundColor: "#e8f5e8",
    color: "#2e7d32",
  },
  vaultIcon: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
  },
  timeIcon: {
    backgroundColor: "#fff3e0",
    color: "#e65100",
  },
  botIcon: {
    backgroundColor: "#f3e5f5",
    color: "#7b1fa2",
  },
  botXpSection: {
    marginTop: "32px",
    paddingTop: "24px",
    borderTop: "2px solid #e0e0e0",
  },
  botXpHeader: {
    marginBottom: "24px",
  },
  leagueField: {
    marginBottom: "16px",
  },
}));

const BasicConfigurationEditor = ({ config, onChange, errors }: any) => {
  const styles = useStyles();
  const [activeUserTimeMinInput, setActiveUserTimeMinInput] = React.useState(
    config.activeUserTimeMin?.toString() || ""
  );

  // Define league types
  const leagueTypes = [
    { key: "STARTER", label: "Starter" },
    { key: "BRONZE", label: "Bronze" },
    { key: "BRONZE_II", label: "Bronze II" },
    { key: "SILVER", label: "Silver" },
    { key: "SILVER_II", label: "Silver II" },
    { key: "GOLD", label: "Gold" },
    { key: "GOLD_II", label: "Gold II" },
    { key: "DIAMOND", label: "Diamond" },
    { key: "DIAMOND_II", label: "Diamond II" },
    { key: "PLATINUM", label: "Platinum" },
  ];

  // Initialize botBaseXp with default values if not present
  const botBaseXp = config.botBaseXp || {
    STARTER: 100,
    BRONZE: 200,
    BRONZE_II: 300,
    SILVER: 450,
    SILVER_II: 600,
    GOLD: 800,
    GOLD_II: 1000,
    DIAMOND: 1300,
    DIAMOND_II: 1600,
    PLATINUM: 2000,
  };

  // Sync local state when config changes from parent
  React.useEffect(() => {
    setActiveUserTimeMinInput(config.activeUserTimeMin?.toString() || "");
  }, [config.activeUserTimeMin]);

  const handleChange = (field, value) => {
    onChange(field, value);
  };

  const handleBotXpChange = (league, value) => {
    const updatedBotBaseXp = {
      ...botBaseXp,
      [league]: parseInt(value) || 0,
    };
    onChange("botBaseXp", updatedBotBaseXp);
  };

  return (
    <Box className={styles.container}>
      <Card className={styles.configCard}>
        <CardContent className={styles.cardContent}>
          <Grid container spacing={4} className={styles.configGrid}>
            {/* Match Win XP */}
            <Grid item xs={12} md={6}>
              <Box className={styles.fieldContainer}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Box
                    className={`${styles.iconContainer} ${styles.matchWinIcon}`}
                  >
                    <EmojiEventsIcon />
                  </Box>
                  <Box>
                    <Typography className={styles.fieldLabel}>
                      Match Win XP
                    </Typography>
                    <Typography className={styles.fieldDescription}>
                      Base XP points awarded for winning a match
                    </Typography>
                  </Box>
                </Box>

                <TextField
                  type="number"
                  value={config.matchWinXP || 0}
                  onChange={(e) =>
                    handleChange("matchWinXP", parseInt(e.target.value) || 0)
                  }
                  className={styles.textField}
                  fullWidth
                  inputProps={{ min: 0, max: 1000 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">XP</InputAdornment>
                    ),
                  }}
                  placeholder="Enter XP amount"
                />

                <Chip
                  label={`Current: ${config.matchWinXP || 0} XP per win`}
                  size="small"
                  className={styles.valueChip}
                />
              </Box>
            </Grid>

            {/* Vault XP Multiplier */}
            <Grid item xs={12} md={6}>
              <Box className={styles.fieldContainer}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Box
                    className={`${styles.iconContainer} ${styles.vaultIcon}`}
                  >
                    <TrendingUpIcon />
                  </Box>
                  <Box>
                    <Typography className={styles.fieldLabel}>
                      Vault XP Multiplier
                    </Typography>
                    <Typography className={styles.fieldDescription}>
                      Multiplier applied to vault-related XP calculations
                    </Typography>
                  </Box>
                </Box>

                <TextField
                  type="number"
                  value={config.vaultXPMultiplier || 0}
                  onChange={(e) =>
                    handleChange(
                      "vaultXPMultiplier",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  className={styles.textField}
                  fullWidth
                  inputProps={{ min: 0, max: 10, step: 0.1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">x</InputAdornment>
                    ),
                  }}
                  placeholder="Enter multiplier"
                />

                <Chip
                  label={`Current: ${
                    config.vaultXPMultiplier || 0
                  }x multiplier`}
                  size="small"
                  className={styles.valueChip}
                />
              </Box>
            </Grid>

            {/* Active User Time Window */}
            <Grid item xs={12} md={6}>
              <Box className={styles.fieldContainer}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Box
                    className={`${styles.iconContainer} ${styles.timeIcon}`}
                  >
                    <AccessTimeIcon />
                  </Box>
                  <Box>
                    <Typography className={styles.fieldLabel}>
                      Active User Time Window
                    </Typography>
                    <Typography className={styles.fieldDescription}>
                      Minutes to look back for active users (0 = use default last week logic)
                    </Typography>
                  </Box>
                </Box>

                <TextField
                  type="number"
                  value={activeUserTimeMinInput}
                  onChange={(e) => {
                    // Allow free typing without validation
                    setActiveUserTimeMinInput(e.target.value);
                  }}
                  onBlur={(e) => {
                    // Only update parent state on blur
                    const value = e.target.value;
                    const numValue = value === "" ? 0 : parseInt(value, 10);
                    handleChange("activeUserTimeMin", isNaN(numValue) ? 0 : numValue);
                  }}
                  className={styles.textField}
                  fullWidth
                  inputProps={{ min: 0, max: 10080 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">min</InputAdornment>
                    ),
                  }}
                  placeholder="Enter minutes (0 for default)"
                />

                <Chip
                  label={
                    config.activeUserTimeMin > 0
                      ? `Active in last ${config.activeUserTimeMin} minutes`
                      : "Using default (last week)"
                  }
                  size="small"
                  className={styles.valueChip}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Bot Base XP Section */}
          <Box className={styles.botXpSection}>
            <Box display="flex" alignItems="center" className={styles.botXpHeader}>
              <Box className={`${styles.iconContainer} ${styles.botIcon}`}>
                <SmartToyIcon />
              </Box>
              <Box>
                <Typography className={styles.fieldLabel} variant="h6">
                  Bot Base XP
                </Typography>
                <Typography className={styles.fieldDescription}>
                  Configure base XP values for bots in each league
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={3}>
              {leagueTypes.map((league) => (
                <Grid item xs={12} sm={6} md={4} key={league.key}>
                  <Box className={styles.leagueField}>
                    <Typography className={styles.fieldLabel} variant="body2">
                      {league.label}
                    </Typography>
                    <TextField
                      type="number"
                      value={botBaseXp[league.key] || 0}
                      onChange={(e) =>
                        handleBotXpChange(league.key, e.target.value)
                      }
                      className={styles.textField}
                      fullWidth
                      inputProps={{ min: 0, max: 10000 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">XP</InputAdornment>
                        ),
                      }}
                      placeholder="Enter base XP"
                      size="small"
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Display errors */}
          {errors && errors.length > 0 && (
            <Box mt={3}>
              {errors.map((error, index) => (
                <Typography key={index} color="error" variant="body2">
                  ⚠️ {error}
                </Typography>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BasicConfigurationEditor;
